// §V2 EXACT PORT — Home (docs/design-spikes/pomela-platform-v2/index.html
// lines 79-287): the cockpit hero (word-stagger headline + spark chips +
// THE composer), "Pick up where you left off" over the REAL recents,
// the 8-card "Start from a spark" template grid (hand-authored minis are
// sanctioned illustrations) with real client-side filtering, a number-free
// honest stat strip and the closing gradhero. The composer is wired to the
// REAL generation session via onPrompt (PromptInput's exact submit/mode/
// attachment machinery).
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { FileSpreadsheet, FileText, Loader2, X } from 'lucide-preact';
import { Ic, tt } from './icons';
import { t } from '../lib/i18n';
import { goNav } from '../lib/nav-items';
import { AppPreview } from '../components/AppPreview';
import type { GenerationMode } from '../components/PromptInput';
import { uploadCreatorAsset, toWire, type UploadedAsset, type CreatorAssetWire } from '../lib/creator-assets';
import { stashComposerDraft, readComposerDraft, clearComposerDraft } from '../lib/signin-draft';
import { getRecentApps, type RecentApp } from '../lib/recent-apps';
import { summarizePrompt } from '../lib/summarize-prompt';

// §launch — Deep build is disabled (Quick and Deep share the same engine; Deep
// isn't ready as its own tier). The old complexity heuristic that auto-promoted
// long/complex prompts to Deep is removed: every build now runs as Quick.

/** Real relative time for the recents rail — the workspace's exact
 *  vocabulary (ov.time.* keys: "just now" / "{n}m ago" / "{n}h ago"). */
function timeAgo(ts: number): string {
  const min = Math.floor((Date.now() - ts) / 60_000);
  if (!Number.isFinite(min) || min < 1) return t('ov.time.now');
  if (min < 60) return t('ov.time.m', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('ov.time.h', { n: h });
  return t('ov.time.d', { n: Math.floor(h / 24) });
}

/** Last-touched stamp (recent-apps' ordering rule). */
function touchedAt(a: RecentApp): number {
  return a.updatedAt ?? a.createdAt;
}

// ─── the spark template card shell (bodies are structurally identical;
//     the hand-authored mini illustration rides in as children) ──────────────
type SparkCat = 'personal' | 'business' | 'hobby' | 'social';

function SparkCard({
  cat,
  catLabel,
  av,
  name,
  tag,
  prompt,
  category,
  hidden,
  onUse,
  children
}: {
  cat: SparkCat;
  catLabel: string;
  av: string;
  name: string;
  tag: string;
  prompt: string;
  /** The genuinely-SUPPORTED Pomela category (category-support.ts) this spark
   *  builds — distinct from `cat` (the personal/business/hobby/social visual
   *  filter). LAUNCH HONESTY: every spark maps to one of the 6 SUPPORTED
   *  categories; surfaced as `data-category` and guarded by
   *  tests/launch-honesty-guard.test.mjs. */
  category: string;
  hidden: boolean;
  onUse: (prompt: string) => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <article
      className="pcard"
      data-filter-cat={cat}
      data-category={category}
      style={hidden ? 'display:none' : undefined}
      onClick={() => onUse(prompt)}
    >
      <div className="pcard-hero">
        <span className="pcard-badge badge dot">{catLabel}</span>
        {children}
      </div>
      <div className="pcard-body">
        <div className="ac-by">
          <span className="ac-av">{av}</span>
          <div><b>{name}</b></div>
        </div>
        <p className="ac-tag">{tag}</p>
        <button
          className="btn btn-brand btn-sm"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUse(prompt);
          }}
        >
          <Ic n="spark" /> {tt('v2.home.useIdea', 'Use this idea')}
        </button>
      </div>
    </article>
  );
}

export function HomeV2({
  onPrompt,
  seed
}: {
  onPrompt: (prompt: string, mode: GenerationMode, assets?: CreatorAssetWire[]) => void;
  seed: string;
}): JSX.Element {
  // ── composer state (PromptInput's exact machinery) ─────────────────────────
  const [value, setValue] = useState('');
  const [mode, setMode] = useState<GenerationMode>('quick');
  const [assets, setAssets] = useState<UploadedAsset[]>([]);
  const [uploading, setUploading] = useState(0);
  const [assetError, setAssetError] = useState('');
  // §F02 — names of files that were attached before a sign-in detour. File
  // blobs can't survive sessionStorage, so on rehydrate we show these and ask
  // the user to re-attach rather than pretending they're still there.
  const [reattachNames, setReattachNames] = useState<string[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLFormElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const trimmed = value.trim();
  // pomela:toast events surface via the single V2Shell ToastHost bridge — no
  // per-screen host here.

  /** The spike's focusComposer — every "start building" affordance lands here. */
  function focusComposer(): void {
    requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      inputRef.current?.focus();
    });
  }

  /** Spark-chip seeding (studio.js: value = label + ' ', focus). */
  function seedChip(label: string): void {
    setValue(label + ' ');
    inputRef.current?.focus();
  }

  /** Template-card seeding — drop the starter prompt in, scroll + focus. */
  function useSpark(prompt: string): void {
    setValue(prompt);
    focusComposer();
  }

  // A `seed` prop change (Pomy "use prompt" / Workspace category starters)
  // primes the canvas + focuses it — PromptInput's
  // exact initialValue re-seed behavior.
  useEffect(() => {
    if (seed && seed !== value) {
      setValue(seed);
      focusComposer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  // §F02 (platform.signin.preserves_prompt) — rehydrate a draft that was stashed
  // before a sign-in detour. CreatorRoot renders every non-home route (#signin,
  // #account, …) by unmounting <App key="root">, which tears down this whole
  // surface and its composer state. On the way back the composer remounts empty
  // unless we restore the stash. Runs ONCE on mount; an explicit `seed` (Pomy /
  // Workspace starters) always wins over a stashed draft.
  useEffect(() => {
    if (seed) return;
    const draft = readComposerDraft();
    if (!draft) return;
    setValue(draft.prompt);
    setMode(draft.mode);
    // Files can't survive the round trip — surface the names for re-attach.
    if (draft.assetNames.length) setReattachNames(draft.assetNames);
    clearComposerDraft(); // consumed
    focusComposer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the in-progress draft as the user composes, so navigating away to
  // ANY non-home route (the real F02 path is home → #account → #signin) never
  // destroys it. Attachment NAMES only — the File blobs are not serializable.
  // The mount pass is skipped so it can never clobber the rehydrate read above,
  // regardless of effect ordering; an emptied composer clears the stash.
  const persistArmed = useRef(false);
  useEffect(() => {
    if (!persistArmed.current) {
      persistArmed.current = true;
      return;
    }
    stashComposerDraft({ prompt: value, mode, assetNames: assets.map((a) => a.name) });
  }, [value, mode, assets]);

  // autoGrow — studio.js's exact cap (min(scrollHeight, 220)).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  }, [value]);

  // §launch — Deep build disabled; never auto-promote to Deep. Builds stay Quick.

  function submit(e?: Event): void {
    e?.preventDefault();
    if (!trimmed || uploading > 0) return;
    onPrompt(trimmed, mode, assets.length ? toWire(assets) : undefined);
    setValue('');
    setAssets([]);
    setAssetError('');
    setReattachNames([]);
    // §F02 — a sent prompt is no longer an unsent draft; drop the stash so a
    // later "create another" opens a clean composer. If the gateway then raises
    // the guest auth-wall, AuthWall re-stashes the prompt for the round trip.
    clearComposerDraft();
  }

  // §L1 attachments — PromptInput's exact upload flow (lib/creator-assets).
  async function pickFiles(list: FileList | null): Promise<void> {
    if (!list?.length) return;
    setAssetError('');
    const room = Math.max(0, 6 - assets.length);
    for (const file of [...list].slice(0, room)) {
      setUploading((n) => n + 1);
      try {
        const a = await uploadCreatorAsset(file);
        setAssets((prev) => [...prev, a]);
      } catch (err) {
        const code = (err as { code?: string })?.code ?? '';
        const key = /media|415|type/i.test(code)
          ? 'pi.attachType'
          : /413|large|size/i.test(code)
            ? 'pi.attachSize'
            : /429|rate|quota/i.test(code)
              ? 'pi.attachRate'
              : 'pi.attachErr';
        setAssetError(t(key));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  // ── "Pick up where you left off" — REAL recents (workspace's source) ───────
  const [recents, setRecents] = useState<RecentApp[]>(() => getRecentApps());
  useEffect(() => {
    const onReset = (): void => setRecents(getRecentApps());
    window.addEventListener('pomela:workspace-reset', onReset);
    return () => window.removeEventListener('pomela:workspace-reset', onReset);
  }, []);

  // ── spark grid filtering — the spike's data-filter behavior, for real ──────
  const [filter, setFilter] = useState<'all' | SparkCat>('all');
  const fpillCls = (v: 'all' | SparkCat): string => `fpill${filter === v ? ' on' : ''}`;
  const sparkHidden = (cat: SparkCat): boolean => filter !== 'all' && filter !== cat;

  // Scroll-reveal — the spike's exact observer (root = #canvas, .12 / -6%);
  // re-arms when late sections (the recents reset) mount.
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;
    if (!('IntersectionObserver' in window)) {
      rootEl.querySelectorAll('.sr,.sr-stagger').forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => {
      rootEl.querySelectorAll('.sr:not(.in),.sr-stagger:not(.in)').forEach((el) => io.observe(el));
    });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [recents.length]);

  const segCls = (m: GenerationMode): string => `seg-i${mode === m ? ' on' : ''}`;
  // LAUNCH HONESTY: every hero spark chip seeds a genuinely-SUPPORTED category
  // (trackers / booking / flashcards / todo-projects). The old "event RSVP" chip
  // (events-rsvp, PLANNED) seeded an unsupported build and was replaced with a
  // to-do list. Guarded by tests/launch-honesty-guard.test.mjs.
  const chipWater = tt('v2.home.chipWater', 'a water tracker');
  const chipBooking = tt('v2.home.chipBooking', 'a booking page');
  const chipFlash = tt('v2.home.chipFlash', 'a flashcard deck');
  const chipTodo = tt('v2.home.chipTodo', 'a to-do list');

  return (
    <section className="screen on" data-screen="home" ref={rootRef}>
      <div className="home-wrap">
        {/* HERO COCKPIT */}
        <div className="cockpit reveal">
          <div className="cockpit-head">
            <h2 className="display">
              <span className="dw">{tt('v2.home.heroW1', 'Type.')}</span>{' '}
              <span className="dw">{tt('v2.home.heroW2', 'Tap.')}</span>
              <br />
              <span className="dw grad">{tt('v2.home.heroW3', 'Install.')}</span>
            </h2>
            <p className="lede">{tt('v2.home.lede', 'Describe it in a sentence. Install it on your phone, no code, no app store.')}</p>
          </div>

          {/* example sparks as their own quiet row, above the composer */}
          <div className="spark-row" id="cmpChips">
            <button type="button" className="chip" onClick={() => seedChip(chipWater)}>
              <Ic n="spark" /> {chipWater}
            </button>
            <button type="button" className="chip" onClick={() => seedChip(chipBooking)}>
              <Ic n="spark" /> {chipBooking}
            </button>
            <button type="button" className="chip" onClick={() => seedChip(chipFlash)}>
              <Ic n="spark" /> {chipFlash}
            </button>
            <button type="button" className="chip" onClick={() => seedChip(chipTodo)}>
              <Ic n="spark" /> {chipTodo}
            </button>
            <button
              type="button"
              className="chip ghost"
              onClick={() => {
                setValue(tt('v2.home.surprisePrompt', 'a workout log with exercises, sets and reps, rest timers and a weekly volume chart'));
                inputRef.current?.focus();
              }}
            >
              <Ic n="dice" /> {tt('v2.home.chipSurprise', 'surprise me')}
            </button>
          </div>

          {/* THE COMPOSER — hero element, the ONE place to start something new */}
          <form className="composer" id="composer" ref={composerRef} onSubmit={submit}>
            <div className="cmp-glow" aria-hidden="true"></div>
            <textarea
              className="cmp-input"
              id="cmpInput"
              ref={inputRef}
              rows={1}
              placeholder={t('pi.placeholder')}
              aria-label={t('pi.whatBuild')}
              value={value}
              onInput={(e) => setValue((e.currentTarget as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Cmd/Ctrl+Enter — always submit (power-user shortcut).
                if (e.metaKey || e.ctrlKey) { e.preventDefault(); submit(e); return; }
                // Shift+Enter — always newline.
                if (e.shiftKey) return;
                // Mobile / touch / narrow viewport → Enter inserts a newline;
                // users submit via the Build button (PromptInput's exact rule).
                const isMobile =
                  typeof window !== 'undefined' &&
                  (window.innerWidth < 768 || window.matchMedia('(pointer: coarse)').matches);
                if (isMobile) return;
                // Desktop plain Enter → submit.
                e.preventDefault();
                submit(e);
              }}
            />

            {/* attached assets — PromptInput's exact previews + remove */}
            {(assets.length > 0 || uploading > 0 || !!assetError) && (
              <div className="flex flex-wrap items-center gap-1.5 px-3 pb-1" aria-live="polite">
                {assets.map((a, ix) => (
                  <span key={`${a.name}-${ix}`} className="inline-flex max-w-[180px] items-center gap-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--bg-subtle)] py-1 pl-2 pr-1 text-[11px] font-medium text-[color:var(--fg-muted)]">
                    {a.kind === 'image' && a.url
                      ? <img src={a.url} alt="" className="h-4 w-4 rounded-[4px] object-cover" />
                      : a.kind === 'rows' ? <FileSpreadsheet size={12} /> : <FileText size={12} />}
                    <span className="truncate">{a.name}{a.kind === 'rows' && a.rowCount ? ` · ${a.rowCount} rows` : ''}</span>
                    <button
                      type="button"
                      aria-label={`${tt('v2.home.removeAsset', 'Remove')} ${a.name}`}
                      onClick={() => setAssets((prev) => prev.filter((_, i) => i !== ix))}
                      className="grid h-4 w-4 place-items-center rounded-full hover:bg-[color:var(--bg-elevated)]"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                {uploading > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border)] py-1 px-2 text-[11px] text-[color:var(--fg-subtle)]">
                    <Loader2 size={11} className="animate-spin" /> {tt('v2.home.uploading', 'Uploading…')}
                  </span>
                )}
                {assetError && <span className="text-[11px] text-[color:var(--color-danger,#e5484d)]">{assetError}</span>}
              </div>
            )}

            {/* §F02 — files can't survive the sign-in round trip; honestly ask
                the user to re-attach the ones they'd picked before signing in. */}
            {reattachNames.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 px-3 pb-1" role="status">
                <span className="text-[11px] text-[color:var(--fg-muted)]">
                  {tt('v2.home.reattach', 'Re-attach to include again:')} {reattachNames.join(', ')}
                </span>
                <button
                  type="button"
                  aria-label={tt('v2.home.reattachDismiss', 'Dismiss re-attach reminder')}
                  onClick={() => setReattachNames([])}
                  className="grid h-4 w-4 place-items-center rounded-full hover:bg-[color:var(--bg-elevated)]"
                >
                  <X size={10} />
                </button>
              </div>
            )}

            <div className="cmp-bar">
              <div className="cmp-left">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp,image/gif,.pdf,.csv,.xlsx,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(e) => void pickFiles((e.currentTarget as HTMLInputElement).files)}
                />
                <button
                  type="button"
                  className="icon-btn sm"
                  title={tt('v2.home.attachTitle', 'Attach a photo or file')}
                  aria-label={tt('v2.home.attachAria', 'Attach')}
                  onClick={() => fileRef.current?.click()}
                >
                  <Ic n="clip" />
                </button>
                <div className="seg" role="tablist" aria-label={tt('v2.home.buildMode', 'Build mode')}>
                  <button type="button" className={segCls('quick')} role="tab" aria-selected={mode === 'quick'} onClick={() => setMode('quick')}>
                    <Ic n="bolt" /><span>{t('pi.quick')}</span>
                  </button>
                  <button
                    type="button"
                    className={`${segCls('deep')} is-soon`}
                    role="tab"
                    aria-selected={false}
                    aria-disabled="true"
                    disabled
                    title={t('pi.deepSoonAria')}
                    aria-label={t('pi.deepSoonAria')}
                  >
                    <Ic n="spark" /><span>{t('pi.deep')}</span>
                    <span className="seg-soon">{t('pi.deepSoon')}</span>
                  </button>
                </div>
              </div>
              <button type="submit" className="cmp-send" aria-label={tt('v2.home.buildIt', 'Build it')} disabled={!trimmed || uploading > 0}>
                <span>{tt('v2.home.buildIt', 'Build it')}</span><Ic n="arrow" />
              </button>
            </div>
          </form>
        </div>

        {/* CONTINUE BUILDING — only when the creator has REAL recent apps */}
        {recents.length > 0 && (
          <section className="block sr gap-breathe">
            <div className="block-head">
              <h3>{tt('v2.home.continueT', 'Pick up where you left off')}</h3>
              <a
                className="link"
                href="#apps"
                onClick={(e) => { e.preventDefault(); goNav('apps'); }}
              >
                {tt('v2.home.openWorkspace', 'Open Workspace')} <Ic n="arrow" />
              </a>
            </div>
            <div className="rail-scroll">
              {recents.map((a) => {
                const name = a.manifest?.name || summarizePrompt(a.prompt);
                // Honest derived status — the workspace's exact rule: 'live'
                // only when a deployed (non-preview) URL exists.
                const live = !!a.url && !a.url.startsWith('/preview/');
                return (
                  <a
                    className="pcard"
                    key={a.appId}
                    href={`#apps/${a.appId}`}
                    onClick={(e) => { e.preventDefault(); goNav('apps/' + a.appId); }}
                  >
                    <div className="pcard-hero">
                      <span className={`pcard-badge badge${live ? ' ok' : ''} dot`}>
                        {live ? t('sb.workspace.fLive') : t('wsx.fDraft')}
                      </span>
                      {/* the spike's hand-built .mini slot → the REAL app, live */}
                      <AppPreview appId={a.appId} url={a.url} ogImageUrl={a.ogImageUrl} />
                    </div>
                    <div className="pcard-body">
                      <div className="ac-by">
                        <span className="ac-av">{(name.charAt(0) || 'P').toUpperCase()}</span>
                        <div>
                          <b>{name}</b>
                          <span>{timeAgo(touchedAt(a))}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* START FROM A SPARK — every tile does real work: tap → prefills the
            composer above (the spike's data-spark-prompt behavior) */}
        <section className="block sr" style="margin-top:12px">
          <div className="block-head">
            <div>
              <h3>{tt('v2.home.sparkT', 'Not sure what to build? Start from a spark.')}</h3>
              <p className="sub-h" style="margin-top:6px">{tt('v2.home.sparkSub', 'Tap any idea to drop it straight into the composer above, edit it, or build it as-is.')}</p>
            </div>
          </div>
          <div className="fpills" data-filter-scope="sparks" style="margin:16px 0 18px">
            <button className={fpillCls('all')} data-filter-value="all" type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
              {tt('v2.home.fAll', 'All')}
            </button>
            <button className={fpillCls('personal')} data-filter-value="personal" type="button" aria-pressed={filter === 'personal'} onClick={() => setFilter('personal')}>
              <Ic n="home" /> {tt('v2.home.fPersonal', 'Personal')}
            </button>
            <button className={fpillCls('business')} data-filter-value="business" type="button" aria-pressed={filter === 'business'} onClick={() => setFilter('business')}>
              <Ic n="chart" /> {tt('v2.home.fBusiness', 'Business')}
            </button>
            <button className={fpillCls('hobby')} data-filter-value="hobby" type="button" aria-pressed={filter === 'hobby'} onClick={() => setFilter('hobby')}>
              <Ic n="bulb" /> {tt('v2.home.fHobby', 'Hobby')}
            </button>
            <button className={fpillCls('social')} data-filter-value="social" type="button" aria-pressed={filter === 'social'} onClick={() => setFilter('social')}>
              <Ic n="msg" /> {tt('v2.home.fSocial', 'Social')}
            </button>
          </div>
          {/* LAUNCH HONESTY: every spark below maps to a genuinely-SUPPORTED
              category (category-support.ts: trackers / booking / workout-logs /
              inventory / flashcards / todo-projects) via the SparkCard `category`
              prop. The prior grid pitched trip-planners / expenses / crm /
              collections / events-rsvp (all PLANNED) as one-click builds — those
              five were replaced. Guarded by tests/launch-honesty-guard.test.mjs. */}
          <div className="apps-grid" data-filter-target="sparks">
            <SparkCard cat="personal" catLabel={tt('v2.home.fPersonal', 'Personal')} av="W" name={tt('v2.home.sparkWaterN', 'Water tracker')} tag={tt('v2.home.sparkWaterTag', 'A glass a day, gently reminded.')} prompt={tt('v2.home.sparkWaterP', 'a water tracker that gently reminds me to drink more water through the day')} category="trackers" hidden={sparkHidden('personal')} onUse={useSpark}>
              <div className="mini" data-scheme="dark" style="--app-accent:#0EA5E9"><div className="mini-top"><span className="mini-mark">W</span><b>{tt('v2.home.miniWaterName', 'Water Tracker')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniWaterH', 'Stay hydrated')}</div></div><div className="mini-ring"><div className="mr" style="--p:62%"></div><div><b>6 / 8</b><span>{tt('v2.home.miniWaterGlasses', 'glasses today')}</span></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="business" catLabel={tt('v2.home.fBusiness', 'Business')} av="S" name={tt('v2.home.sparkBookN', 'Studio bookings')} tag={tt('v2.home.sparkBookTag', 'Take appointments and deposits, no phone tag.')} prompt={tt('v2.home.sparkBookP', 'a booking page for my studio with deposits and appointment reminders')} category="booking" hidden={sparkHidden('business')} onUse={useSpark}>
              <div className="mini" data-scheme="dark" style="--app-accent:#7C3AED"><div className="mini-top"><span className="mini-mark">S</span><b>{tt('v2.home.miniBookName', 'Studio Bookings')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniBookH', 'Book a time')}</div></div><div className="mini-cal"><i>M</i><i className="on">T</i><i>W</i><i className="on">T</i><i>F</i><i>S</i><i>S</i></div></div><div className="mini-tabs"><i className="on"></i><i></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="hobby" catLabel={tt('v2.home.fHobby', 'Hobby')} av="L" name={tt('v2.home.sparkLangN', 'Language streak')} tag={tt('v2.home.sparkLangTag', '10 minutes a day, tracked like a habit.')} prompt={tt('v2.home.sparkLangP', 'a daily streak tracker for 10 minutes of language practice')} category="trackers" hidden={sparkHidden('hobby')} onUse={useSpark}>
              <div className="mini" data-scheme="light" style="--app-accent:#D97706"><div className="mini-top"><span className="mini-mark">L</span><b>{tt('v2.home.miniLangName', 'Language Streak')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniLangH', 'Daily practice')}</div></div><div className="mini-ring"><div className="mr" style="--p:80%"></div><div><b>{tt('v2.home.miniLangDays', '14 day')}</b><span>{tt('v2.home.miniLangStreak', 'streak')}</span></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="social" catLabel={tt('v2.home.fSocial', 'Social')} av="C" name={tt('v2.home.sparkClassN', 'Class booking')} tag={tt('v2.home.sparkClassTag', 'Share one link — people pick a time and reserve a spot.')} prompt={tt('v2.home.sparkClassP', 'a class booking page where people pick an available time and reserve a spot')} category="booking" hidden={sparkHidden('social')} onUse={useSpark}>
              <div className="mini" data-scheme="dark" style="--app-accent:#DB2777"><div className="mini-top"><span className="mini-mark">C</span><b>{tt('v2.home.miniClassName', 'Class Booking')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniClassH', 'Reserve a spot')}</div></div><div className="mini-cal"><i>M</i><i className="on">T</i><i>W</i><i className="on">T</i><i>F</i><i>S</i><i>S</i></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="personal" catLabel={tt('v2.home.fPersonal', 'Personal')} av="G" name={tt('v2.home.sparkWorkoutN', 'Workout log')} tag={tt('v2.home.sparkWorkoutTag', 'Log sets and reps, watch your weekly volume climb.')} prompt={tt('v2.home.sparkWorkoutP', 'a workout log with exercises, sets and reps, rest timers and a weekly volume chart')} category="workout-logs" hidden={sparkHidden('personal')} onUse={useSpark}>
              <div className="mini" data-scheme="light" style="--app-accent:#16A34A"><div className="mini-top"><span className="mini-mark">G</span><b>{tt('v2.home.miniWorkoutName', 'Workout Log')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniWorkoutH', 'This week')}</div></div><div className="mini-tiles"><div className="mini-tile"><b>12</b><span>{tt('v2.home.miniWorkoutSets', 'Sets')}</span></div><div className="mini-tile"><b>880kg</b><span>{tt('v2.home.miniWorkoutVol', 'Volume')}</span></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="business" catLabel={tt('v2.home.fBusiness', 'Business')} av="I" name={tt('v2.home.sparkInvN', 'Inventory tracker')} tag={tt('v2.home.sparkInvTag', 'Every item, its count, and what is running low.')} prompt={tt('v2.home.sparkInvP', 'an inventory tracker for my shop with item counts, stock adjustments and low-stock alerts')} category="inventory" hidden={sparkHidden('business')} onUse={useSpark}>
              <div className="mini" data-scheme="light" style="--app-accent:#4F46E5"><div className="mini-top"><span className="mini-mark">I</span><b>{tt('v2.home.miniInvName', 'Inventory')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniInvH', 'Items')}</div></div><div className="mini-list"><div className="mini-li"><span className="mav"></span><div><b>{tt('v2.home.miniInvI1', 'Oat milk')}</b><span>{tt('v2.home.miniInvSub1', '12 in stock')}</span></div><span className="mdot"></span></div><div className="mini-li"><span className="mav"></span><div><b>{tt('v2.home.miniInvI2', 'Espresso beans')}</b><span>{tt('v2.home.miniInvSub2', 'Low · 3 left')}</span></div></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="hobby" catLabel={tt('v2.home.fHobby', 'Hobby')} av="F" name={tt('v2.home.sparkFlashN', 'Flashcard deck')} tag={tt('v2.home.sparkFlashTag', 'Study with spaced repetition, one card at a time.')} prompt={tt('v2.home.sparkFlashP', 'a flashcard deck with spaced-repetition study, decks by topic and a daily review')} category="flashcards" hidden={sparkHidden('hobby')} onUse={useSpark}>
              <div className="mini" data-scheme="dark" style="--app-accent:#0F9D6E"><div className="mini-top"><span className="mini-mark">F</span><b>{tt('v2.home.miniFlashName', 'Flashcards')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniFlashH', 'Spanish 101')}</div></div><div className="mini-tiles"><div className="mini-tile"><b>48</b><span>{tt('v2.home.miniFlashCards', 'Cards')}</span></div><div className="mini-tile"><b>12</b><span>{tt('v2.home.miniFlashDue', 'Due today')}</span></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
            <SparkCard cat="personal" catLabel={tt('v2.home.fPersonal', 'Personal')} av="T" name={tt('v2.home.sparkTodoN', 'To-do list')} tag={tt('v2.home.sparkTodoTag', 'Projects, checklists and due dates that actually stick.')} prompt={tt('v2.home.sparkTodoP', 'a to-do app with projects, checklists, due dates and quick notes')} category="todo-projects" hidden={sparkHidden('personal')} onUse={useSpark}>
              <div className="mini" data-scheme="light" style="--app-accent:#0891B2"><div className="mini-top"><span className="mini-mark">T</span><b>{tt('v2.home.miniTodoName', 'To-dos')}</b></div><div className="mini-body"><div><div className="mini-h">{tt('v2.home.miniTodoH', 'Today')}</div></div><div className="mini-tiles"><div className="mini-tile"><b>3</b><span>{tt('v2.home.miniTodoDone', 'Done')}</span></div><div className="mini-tile"><b>5</b><span>{tt('v2.home.miniTodoOpen', 'To-do')}</span></div></div></div><div className="mini-tabs"><i className="on"></i><i></i></div></div>
            </SparkCard>
          </div>
        </section>

        {/* QUIET STAT STRIP — the spike's fabricated community numbers are
            replaced with real, number-free product facts (same visual) */}
        <div className="stat-grid sr">
          <div className="stat"><span className="s-ico"><Ic n="rocket" /></span><div className="s-num">{tt('v2.home.stat1N', 'Installs')}</div><div className="s-lbl">{tt('v2.home.stat1L', 'like a real app')}</div></div>
          <div className="stat"><span className="s-ico"><Ic n="bolt" /></span><div className="s-num">{tt('v2.home.stat2N', 'Edit anytime')}</div><div className="s-lbl">{tt('v2.home.stat2L', 'with Pomy, your AI copilot')}</div></div>
          <div className="stat"><span className="s-ico"><Ic n="grid" /></span><div className="s-num">{tt('v2.home.stat3N', 'Real data')}</div><div className="s-lbl">{tt('v2.home.stat3L', 'server-backed, yours to keep')}</div></div>
          <div className="stat"><span className="s-ico"><Ic n="check" /></span><div className="s-num">{tt('v2.home.stat4N', 'No code')}</div><div className="s-lbl">{tt('v2.home.stat4L', 'describe it in one sentence')}</div></div>
        </div>

        {/* CLOSING INVITATION — its only job is to send you back to the composer */}
        <div className="gradhero sr" style="text-align:center;padding:40px 24px">
          <span className="gh-ey">{tt('v2.home.ghEyebrow', 'Ready when you are')}</span>
          <h3 className="title-hero">{tt('v2.home.ghTitle', 'Your idea is one sentence away.')}</h3>
          <p style="margin:0 auto;max-width:46ch">{tt('v2.home.ghBody', 'No code, no waiting, no app store review. Just describe it.')}</p>
          <div className="gh-link" style="justify-content:center;margin-top:16px">
            <button className="btn btn-solid" type="button" onClick={focusComposer}>
              <Ic n="bolt" /> {tt('v2.home.ghCta', 'Start building')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
